import { useEffect, useState } from 'react';

/**
 * Keep the screen awake while cooking (Screen Wake Lock API). The lock is
 * dropped by the browser whenever the tab is hidden, so it is re-acquired on
 * `visibilitychange`. Unsupported browsers (Safari < 16.4, Firefox) simply
 * report `supported: false` — the screen sleeps and nothing else changes.
 */
export function useWakeLock(enabled: boolean): { supported: boolean; active: boolean } {
  const supported =
    typeof navigator !== 'undefined' && 'wakeLock' in navigator && navigator.wakeLock !== undefined;
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!supported || !enabled) return;
    let sentinel: WakeLockSentinel | null = null;
    let cancelled = false;

    const release = () => {
      const current = sentinel;
      sentinel = null;
      setActive(false);
      if (current) void current.release().catch(() => undefined);
    };

    const acquire = async () => {
      if (cancelled || document.visibilityState !== 'visible' || sentinel) return;
      try {
        const lock = await navigator.wakeLock.request('screen');
        if (cancelled) {
          void lock.release().catch(() => undefined);
          return;
        }
        sentinel = lock;
        setActive(true);
        lock.addEventListener('release', () => {
          if (sentinel === lock) {
            sentinel = null;
            setActive(false);
          }
        });
      } catch {
        // Denied (battery saver, no user gesture yet) — try again on the next
        // visibility change rather than nagging.
        setActive(false);
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void acquire();
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      release();
    };
  }, [supported, enabled]);

  return { supported, active };
}
